import { toast } from 'sonner';
import i18n from '@/i18n';

/**
 * Utility functions for downloading files with consistent error handling and filename generation
 */

/**
 * Sanitizes text for use in filenames
 * @param text - The text to sanitize
 * @returns Sanitized text safe for filenames
 */
export const sanitizeFilename = (text: string): string => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

/**
 * Determines file extension based on MIME type
 * @param mimeType - The MIME type of the file
 * @returns Appropriate file extension
 */
export const getFileExtension = (mimeType: string): string => {
  if (!mimeType) return '.pdf'; // default
  
  const mime = mimeType.toLowerCase();
  
  if (mime.includes('pdf')) return '.pdf';
  if (mime.includes('word') || mime.includes('docx')) return '.docx';
  if (mime.includes('msword') || mime.includes('doc')) return '.doc';
  if (mime.includes('markdown') || mime === 'text/markdown') return '.md';
  if (mime.includes('text/plain')) return '.txt';
  if (mime.includes('text')) {
    const parts = mime.split('/');
    return parts.length > 1 && parts[1] ? `.${parts[1]}` : '.txt';
  }
  if (mime === 'application/octet-stream') return '.txt';
  
  // For other types, try to extract the subtype
  const parts = mime.split('/');
  return parts.length > 1 && parts[1] ? `.${parts[1]}` : '.pdf';
};

/**
 * Downloads a blob as a file with proper error handling
 * @param blob - The blob to download
 * @param filename - The desired filename
 * @param successMessage - Success message to show in toast
 * @param errorMessage - Error message to show in toast
 */
export const downloadBlob = (
  blob: Blob,
  filename: string,
  successMessage?: string,
  errorMessage?: string,
): void => {
  const resolvedErrorMessage = errorMessage ?? i18n.t('common:toasts.download_failed');
  try {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    if (successMessage) {
      toast.success(successMessage);
    }
  } catch (error) {
    console.error('Error downloading file:', error);
    toast.error(resolvedErrorMessage);
  }
};

/**
 * Handles download errors with appropriate user feedback
 * @param error - The error that occurred
 * @param defaultMessage - Default error message
 */
export const handleDownloadError = (
  error: unknown,
  defaultMessage?: string,
  options?: { notFound?: string; forbidden?: string },
): void => {
  console.error('Download error:', error);

  const resolvedDefault = defaultMessage ?? i18n.t('common:toasts.download_failed');
  const notFoundMessage = options?.notFound ?? i18n.t('common:toasts.document_not_found');
  const forbiddenMessage = options?.forbidden ?? i18n.t('common:toasts.document_forbidden');
  const unexpectedMessage = i18n.t('common:toasts.unexpected_error');
  const enUnexpected = i18n.t('common:toasts.unexpected_error', { lng: 'en' });

  if (error instanceof Error) {
    if (error.message.includes('404')) {
      toast.error(notFoundMessage);
    } else if (error.message.includes('403')) {
      toast.error(forbiddenMessage);
    } else {
      const message = error.message.trim();
      toast.error(
        message && message !== enUnexpected && message !== resolvedDefault
          ? message
          : unexpectedMessage,
      );
    }
  } else {
    toast.error(resolvedDefault);
  }
};

/**
 * Creates a filename for job-related documents
 * @param jobTitle - The job title
 * @param companyName - The company name
 * @param documentType - Type of document (e.g., 'job_description', 'job_offer')
 * @param extension - File extension
 * @returns Formatted filename
 */
export const createJobDocumentFilename = (
  jobTitle: string,
  companyName: string,
  documentType: string,
  extension: string
): string => {
  const sanitizedTitle = sanitizeFilename(jobTitle);
  const sanitizedCompany = sanitizeFilename(companyName || 'company');
  return `${sanitizedTitle}_${sanitizedCompany}_${documentType}${extension}`;
};
